import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import SettingsPage from "./pages/SettingsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import NewProjectPage from "./pages/NewProjectPage";
import TemplatesPage from "./pages/TemplatesPage";
import ComparePage from "./pages/ComparePage";
import CustomAgentsPage from "./pages/CustomAgentsPage";
import PromoPage from "./pages/PromoPage";
import AdminPage from "./pages/AdminPage";
import ReferralPage from "./pages/ReferralPage";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import SocialAuthCallback from "./pages/SocialAuthCallback";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider } from "./context/AuthContext";
import { useAuth } from "./hooks/useAuth";

// Router component
function AppRouter() {
  const location = useLocation();
  
  // Handle Google OAuth callback (early return for non-route rendering)
  if (location.pathname === '/auth/callback' && location.search.includes('code=')) {
    return <GoogleAuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth/callback" element={<GoogleAuthCallback />} />
      <Route path="/auth/callback/:provider" element={<SocialAuthCallback />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/analytics" 
        element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/new-project" 
        element={
          <ProtectedRoute>
            <NewProjectPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/templates" 
        element={
          <ProtectedRoute>
            <TemplatesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/compare" 
        element={
          <ProtectedRoute>
            <ComparePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/promo" 
        element={
          <ProtectedRoute>
            <PromoPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/agents" 
        element={
          <ProtectedRoute>
            <CustomAgentsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/referral" 
        element={
          <ProtectedRoute>
            <ReferralPage />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // If user data passed from AuthCallback, render immediately
  if (location.state?.user) {
    return children;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthProvider>
  );
}

export default App;
